// Organização de leitura apenas. Valores, status e permissões vêm do dashboard oficial.
export const UNKNOWN_YEAR = 'sem-ano';
export const CATEGORIES = [
  { id: 'mensalidade', label: 'Mensalidades' },
  { id: 'material', label: 'Material didático' },
  { id: 'bilingue', label: 'Bilíngue' },
  { id: 'integral', label: 'Integral/Almoço' },
  { id: 'matricula', label: 'Matrícula/Rematrícula' },
  { id: 'outros', label: 'Outros' },
];
export const FILTERS = [
  { id: 'todos', label: 'Todos' }, { id: 'aberto', label: 'Em aberto' },
  { id: 'vencido', label: 'Vencidos' }, { id: 'pago', label: 'Pagos' },
];
export const fmtBRL = value => value == null ? '—' : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
export const fmtDate = value => {
  if (!value) return 'Não informada';
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : String(value);
};
export const yearLabel = year => year === UNKNOWN_YEAR ? 'Ano letivo não informado' : year;
export const isOpen = t => ['pendente', 'vencido', 'parcial'].includes(t.status);
export const matchesFilter = (t, filter) => filter === 'todos' || (filter === 'aberto' ? isOpen(t) : t.status === filter);

const explicitYear = (source, includeLegacy = false) => {
  for (const value of [source?.academic_year, source?.school_year, source?.ano_letivo, includeLegacy ? source?.year : null]) {
    if (/^(19|20|21)\d{2}$/.test(String(value ?? '').trim())) return String(value).trim();
  }
  return null;
};
export const academicYear = (title, metadata = {}) => (
  explicitYear(title) || explicitYear(metadata) || explicitYear(title.contract, true) ||
  explicitYear(title.contrato, true) || explicitYear(metadata.contract, true) ||
  explicitYear(metadata.contrato, true) || explicitYear(title, true) || explicitYear(metadata, true) || UNKNOWN_YEAR
);
const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
export const categoryId = (title, metadata = {}) => {
  const category = normalize(title.categoria || metadata.category || metadata.category_id || metadata.categoria_id || title.descricao);
  if (/rematric|matric/.test(category)) return 'matricula';
  if (/bilin/.test(category)) return 'bilingue';
  if (/integral|almoco/.test(category)) return 'integral';
  if (/material|didatic/.test(category)) return 'material';
  if (/mensal|anuidade/.test(category)) return 'mensalidade';
  return 'outros';
};

// Join estrito por aluno + título. Nenhum valor/status do endpoint legado substitui o DTO.
export const organizeTitles = (dashboards, metadata) => {
  const index = new Map((metadata || []).map(t => [`${t.student_id}:${t.id}`, t]));
  return dashboards.flatMap(({ aluno, titulos = [] }) => titulos.map(t => {
    const raw = index.get(`${aluno.id}:${t.id}`) || {};
    const installment = t.parcela ?? raw.installment_number ?? null;
    const total = t.total_parcelas ?? raw.total_installments ?? raw.installment_total ?? raw.installments_total;
    return {
      ...t,
      view: {
        studentId: aluno.id, studentName: aluno.nome, key: `${aluno.id}-${t.id}`,
        year: academicYear(t, raw), category: categoryId(t, raw),
        installment: installment == null ? null : (total && !String(installment).includes('/') ? `${installment}/${total}` : String(installment)),
      },
    };
  }));
};

// Somente somas de exibição, em centavos; não calcula descontos, juros ou saldo do título.
const sum = (titles, field) => titles.reduce((total, t) => total + Math.round(Number(t[field] || 0) * 100), 0) / 100;
export const summarize = titles => {
  const open = titles.filter(isOpen);
  const paid = titles.filter(t => t.status === 'pago');
  const active = titles.filter(t => isOpen(t) || t.status === 'pago');
  return {
    count: titles.length, openCount: open.length, paidCount: paid.length,
    otherCount: titles.length - active.length,
    open: sum(open, 'valor_atual'), overdue: sum(titles.filter(t => t.status === 'vencido'), 'valor_atual'),
    paid: sum(paid, 'valor_pago'), total: sum(active, 'valor_atual'),
  };
};
export const groupYears = titles => {
  const groups = new Map();
  titles.forEach(t => groups.set(t.view.year, [...(groups.get(t.view.year) || []), t]));
  return [...groups].sort(([a], [b]) => a === UNKNOWN_YEAR ? 1 : b === UNKNOWN_YEAR ? -1 : Number(b) - Number(a))
    .map(([year, items]) => ({ year, titles: items, summary: summarize(items) }));
};
export const defaultYear = years => (
  years.find(y => y.year !== UNKNOWN_YEAR && y.summary.openCount > 0) ||
  years.find(y => y.year !== UNKNOWN_YEAR && y.summary.paidCount > 0) || years[0]
)?.year || '';
export const groupCategories = (titles, filter) => CATEGORIES.map(category => {
  const all = titles.filter(t => t.view.category === category.id);
  const visible = all.filter(t => matchesFilter(t, filter)).sort((a, b) => (
    String(a.vencimento || '9999').localeCompare(String(b.vencimento || '9999')) || a.view.key.localeCompare(b.view.key)
  ));
  return { ...category, titles: visible, summary: summarize(all) };
}).filter(category => category.titles.length);